import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Grid, Chip, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import {
  CheckCircle, ArrowForward, ExpandMore,
  Lock, Shield, VerifiedUser, Backup,
  AdminPanelSettings, VpnKey, Email, Storage,
} from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';

const stats = [
  { value: 'TLS 1.3', label: 'Encryption in Transit' },
  { value: 'AES-256', label: 'Encryption at Rest' },
  { value: '100%', label: 'Indian Data Residency' },
  { value: 'Full', label: 'Audit Logging' },
];

const pillars = [
  {
    color: '#dc2626',
    icon: <Lock sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'End-to-End Data Privacy',
    desc: "All email data is stored exclusively in our Indian datacenter — never on third-party cloud services. Your organisation's communication stays under your control, always.",
    points: [
      'Data stored in India (DPDP Act 2023 aligned)',
      'AES-256 encryption at rest',
      'TLS 1.3 for all connections in transit',
      'No third-party data sharing or processing',
    ],
  },
  {
    color: '#7c3aed',
    icon: <Backup sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Full Audit Trail',
    desc: 'Every admin action — login, password change, user creation, permission change — is logged with a timestamp and IP address. Logs are tamper-evident and exportable on demand.',
    points: [
      'Admin action log (who did what, when)',
      'User login and session history',
      'Tamper-evident log storage',
      'Export logs for compliance review',
    ],
  },
  {
    color: '#059669',
    icon: <Shield sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Authentication & Access Control',
    desc: 'Enforce 2FA organisation-wide from the admin panel. Role-based access ensures staff can only access what they need — no over-privileged accounts.',
    points: [
      'Org-wide 2FA policy enforcement',
      'Role-based access control (RBAC)',
      'Session management & remote revoke',
      'IP allowlist for admin access',
    ],
  },
];

const features = [
  { icon: <VerifiedUser sx={{ fontSize: 22, color: '#dc2626' }} />, title: 'DPDP Act 2023 Aligned', desc: 'Data stored in India. User consent controls aligned with DPDP requirements.' },
  { icon: <AdminPanelSettings sx={{ fontSize: 22, color: '#7c3aed' }} />, title: 'Admin-Controlled Access', desc: 'Grant and revoke access without touching a server. Fully dashboard-managed.' },
  { icon: <VpnKey sx={{ fontSize: 22, color: '#059669' }} />, title: 'Two-Factor Authentication', desc: 'Enforce 2FA across all staff — with or without smartphones (TOTP support).' },
  { icon: <Email sx={{ fontSize: 22, color: '#0891b2' }} />, title: 'SPF / DKIM / DMARC', desc: 'Auto-configured per domain. Spoofing and phishing protection built in.' },
  { icon: <Storage sx={{ fontSize: 22, color: '#d97706' }} />, title: 'Encrypted Storage', desc: 'Mailbox data encrypted at rest with AES-256. Backups encrypted separately.' },
  { icon: <Lock sx={{ fontSize: 22, color: '#dc2626' }} />, title: 'Quarantine & Phishing Filter', desc: 'Rspamd-powered spam and phishing detection. Suspicious emails quarantined automatically.' },
];

const faqs = [
  { q: 'Where is our email data stored?', a: 'All email data is stored exclusively in CruvzMail\'s own datacenter infrastructure within India. We do not use AWS, Azure, or any third-party cloud storage. Your organisation\'s data never leaves the country.' },
  { q: 'Can we get an audit log of all admin actions?', a: 'Yes. Every admin action — user creation, password reset, permission change, login — is logged with timestamp, IP address, and acting admin. Logs are available from the admin panel and can be exported as CSV for internal or external audits.' },
  { q: 'Do you support two-factor authentication?', a: 'Yes. Admins can enforce 2FA organisation-wide or on a per-user basis from the admin panel. CruvzMail supports TOTP-based authenticator apps (Google Authenticator, Authy, Microsoft Authenticator) at no additional cost.' },
  { q: 'Can we restrict which IP addresses can access the admin panel?', a: 'Yes. IP allowlisting is available for admin panel access. External login attempts from non-whitelisted IPs are blocked before credentials are even checked.' },
  { q: 'Is CruvzMail compliant with the DPDP Act 2023?', a: 'CruvzMail is designed with DPDP Act 2023 principles in mind: data is stored in India, access is controlled and logged, and individual user data can be deleted on request. We recommend reviewing the requirements with your legal team to confirm alignment for your specific use case.' },
];

const HealthcarePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout activeNav="For Business">

      {/* ── HERO ── */}
      <Box sx={{
        pt: { xs: '120px', md: '145px' }, pb: { xs: 10, md: 14 }, px: { xs: 2, md: 5 },
        position: 'relative', overflow: 'hidden', textAlign: 'center',
        background: 'linear-gradient(160deg, #0f172a 0%, #1a0f2e 40%, #0a1628 100%)',
      }}>
        <Box sx={{ position: 'absolute', top: -180, right: -120, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(220,38,38,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -80, left: -80, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,58,237,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 860, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Chip label="Healthcare" sx={{ mb: 3, bgcolor: 'rgba(220,38,38,0.15)', color: '#fca5a5', fontWeight: 700, fontSize: '0.78rem', border: '1px solid rgba(220,38,38,0.3)', letterSpacing: '0.06em' }} />
          <Typography variant="h1" sx={{
            fontWeight: 900, fontSize: { xs: '2.4rem', md: '3.6rem' }, lineHeight: 1.1, mb: 3,
            background: 'linear-gradient(135deg, #ffffff 30%, #fca5a5 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            Secure Email for<br />Healthcare Organisations
          </Typography>
          <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, color: 'rgba(255,255,255,0.6)', maxWidth: 640, mx: 'auto', lineHeight: 1.85, mb: 5 }}>
            Data stays in India. Every access is logged. Encryption at rest and in transit — with the admin controls your organisation needs to stay accountable and compliant.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/contact')} endIcon={<ArrowForward />} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', bgcolor: '#dc2626', boxShadow: '0 0 40px rgba(220,38,38,0.4)', '&:hover': { bgcolor: '#b91c1c' } }}>
              Contact Us
            </Button>
            <Button onClick={() => navigate('/security')} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              View Security Details
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── STATS ── */}
      <Box sx={{ bgcolor: '#0f172a', py: 3.5, px: { xs: 2, md: 5 }, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Grid container spacing={2} justifyContent="center">
            {stats.map((s) => (
              <Grid size={{ xs: 6, md: 3 }} key={s.label}>
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.3rem', md: '1.6rem' }, color: '#fca5a5', lineHeight: 1 }}>{s.value}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500, mt: 0.5 }}>{s.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── PILLARS ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Why Healthcare Chooses Us</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 1.5 }}>
              Security and Control at Every Layer
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '1rem', maxWidth: 520, mx: 'auto' }}>
              Built for organisations where accountability, data protection, and access control are non-negotiable.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {pillars.map((p, i) => (
              <Grid size={{ xs: 12, md: 4 }} key={p.title}>
                <Box sx={{
                  height: '100%', p: { xs: 3, md: 4 }, borderRadius: 3.5,
                  border: '1px solid #e2e8f0', bgcolor: '#fff', position: 'relative', overflow: 'hidden',
                  transition: 'all 0.25s',
                  '&:hover': { borderColor: p.color, boxShadow: `0 8px 40px ${p.color}12`, transform: 'translateY(-3px)' },
                }}>
                  <Typography sx={{ position: 'absolute', top: -14, right: 14, fontWeight: 900, fontSize: '5.5rem', color: `${p.color}09`, lineHeight: 1, userSelect: 'none', pointerEvents: 'none' }}>
                    {String(i + 1).padStart(2, '0')}
                  </Typography>
                  <Box sx={{ width: 50, height: 50, borderRadius: 2.5, bgcolor: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5, boxShadow: `0 6px 20px ${p.color}30` }}>
                    {p.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a', mb: 1.2 }}>{p.title}</Typography>
                  <Typography sx={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.8, mb: 2.5 }}>{p.desc}</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                    {p.points.map((pt) => (
                      <Box key={pt} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ fontSize: 15, color: p.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.82rem', color: '#475569', fontWeight: 500 }}>{pt}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── FEATURES ── */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: { xs: 2, md: 5 }, bgcolor: '#fff' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.7rem', md: '2.2rem' }, mb: 1 }}>Security Capabilities</Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.95rem' }}>Every feature below is included in every plan — no security add-ons needed.</Typography>
          </Box>
          <Grid container spacing={2.5}>
            {features.map((f) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.title}>
                <Box sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: '14px', height: '100%', bgcolor: '#fafafa', transition: 'all 0.2s', '&:hover': { borderColor: '#cbd5e1', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' } }}>
                  <Box sx={{ mb: 1.5 }}>{f.icon}</Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', mb: 0.6 }}>{f.title}</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.7 }}>{f.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── FAQ ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.7rem', md: '2.2rem' } }}>Healthcare FAQ</Typography>
          </Box>
          {faqs.map((faq, i) => (
            <Accordion key={i} elevation={0} disableGutters sx={{ border: '1px solid #e2e8f0', borderRadius: '12px !important', mb: 1.5, overflow: 'hidden', '&:before': { display: 'none' }, '&.Mui-expanded': { boxShadow: '0 4px 20px rgba(0,0,0,0.06)', borderColor: '#fecaca' } }}>
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#64748b' }} />} sx={{ px: 3, py: 0.5, '&.Mui-expanded': { bgcolor: '#fffbfb' } }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f172a', py: 0.5 }}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 2.5, bgcolor: '#fffbfb' }}>
                <Typography sx={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.85 }}>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ py: { xs: 10, md: 12 }, px: { xs: 2, md: 6 }, bgcolor: '#0f172a', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(220,38,38,0.08) 0%, transparent 60%)', pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 620, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '2.8rem' }, color: '#fff', mb: 2, lineHeight: 1.15 }}>
            Email Your Organisation<br />Can Trust
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)', mb: 5, lineHeight: 1.85 }}>
            Data in India. Full audit logs. Encryption at every layer. Talk to our team about your specific requirements.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/contact')} endIcon={<ArrowForward />} sx={{ px: 4.5, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
              Talk to Our Team
            </Button>
            <Button onClick={() => navigate('/security')} sx={{ px: 4.5, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              Security Overview
            </Button>
          </Box>
        </Box>
      </Box>

    </PublicLayout>
  );
};

export default HealthcarePage;
